// Client-side Web Push subscription flow.
import { api } from "./api";
import type { PushSubscriptionJSON } from "@hh/shared";

function urlB64ToUint8Array(base64: string): Uint8Array {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export function pushSupported(): boolean {
  return "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
}

export async function enablePush(): Promise<{ ok: boolean; reason?: string }> {
  if (!pushSupported()) return { ok: false, reason: "This device/browser doesn't support web push. On iPhone, install the app to your Home Screen first." };

  const perm = await Notification.requestPermission();
  if (perm !== "granted") return { ok: false, reason: "Notification permission was denied." };

  const { key } = await api.pushPublicKey();
  if (!key) return { ok: false, reason: "Push isn't configured on the server yet (VAPID keys)." };

  const reg = await navigator.serviceWorker.ready;
  const existing = await reg.pushManager.getSubscription();
  const sub = existing ?? (await reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlB64ToUint8Array(key) as BufferSource,
  }));
  await api.pushSubscribe(sub.toJSON() as PushSubscriptionJSON);
  return { ok: true };
}
